// Story 7.10 — the "New post" card at the top of the creator dashboard.
//
// One card, four modes (Sally's segmented-picker call): Text ·
// Text + image · Twitch clip · YouTube short. Submits to Story 7.11's
// POST /api/v1/creators/me/posts. No drafts, no edit-after-publish, no
// floating "+" anywhere else — the dashboard is the creator's home and
// this card is the only authoring surface at v1.
//
// Deliberately DARK (GreenholdFrame chrome — forest-800 + antique-gold
// hairline) on the light dashboard: the posting surface previews the
// feed's visual world (AC 1).

import { useRef, useState, type ChangeEvent, type FormEvent } from "react";
import toast from "react-hot-toast";

import { Button } from "@greenhold/components";

import { useCreatePost, type CreatePostVariables } from "./useCreatePost";
import { useUploadMediaBlob } from "./useUploadMediaBlob";

const BODY_MAX = 280;

type PostKind = "text" | "text_image" | "twitch_clip" | "youtube_short";

const KIND_TABS: Array<{ kind: PostKind; label: string }> = [
  { kind: "text", label: "Text" },
  { kind: "text_image", label: "Text + image" },
  { kind: "twitch_clip", label: "Twitch clip" },
  { kind: "youtube_short", label: "YouTube short" },
];

/** Mirrors the server whitelist (client-side is UX only — AC 4). */
const TWITCH_CLIP_PATTERNS = [
  /^https:\/\/(www\.)?twitch\.tv\/[^/]+\/clip\/[a-zA-Z0-9\-_]+$/,
  /^https:\/\/clips\.twitch\.tv\/[a-zA-Z0-9\-_]+$/,
  /^https:\/\/m\.twitch\.tv\/clip\/[a-zA-Z0-9\-_]+$/,
];
const YOUTUBE_SHORT_PATTERNS = [
  /^https:\/\/(www\.)?youtube\.com\/shorts\/[a-zA-Z0-9\-_]+$/,
  /^https:\/\/youtu\.be\/[a-zA-Z0-9\-_]+$/,
];

export function isTwitchClipUrl(url: string): boolean {
  return TWITCH_CLIP_PATTERNS.some((re) => re.test(url));
}

export function isYoutubeShortUrl(url: string): boolean {
  return YOUTUBE_SHORT_PATTERNS.some((re) => re.test(url));
}

export function NewPostCard() {
  const [kind, setKind] = useState<PostKind>("text");
  const [body, setBody] = useState("");
  const [clipUrl, setClipUrl] = useState("");
  const [blobPath, setBlobPath] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const createPost = useCreatePost();
  const uploader = useUploadMediaBlob();

  const overLimit = body.length > BODY_MAX;
  const isClipKind = kind === "twitch_clip" || kind === "youtube_short";
  const clipUrlValid =
    kind === "twitch_clip"
      ? isTwitchClipUrl(clipUrl)
      : kind === "youtube_short"
        ? isYoutubeShortUrl(clipUrl)
        : true;
  const showClipUrlError = isClipKind && clipUrl.length > 0 && !clipUrlValid;

  const submitDisabled =
    createPost.isPending ||
    uploader.isUploading ||
    overLimit ||
    (kind === "text" && body.trim().length === 0) ||
    (kind === "text_image" && (body.trim().length === 0 || !blobPath)) ||
    (isClipKind && !clipUrlValid);

  const resetForm = () => {
    setKind("text");
    setBody("");
    setClipUrl("");
    setBlobPath(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setInlineError(null);
    uploader.reset();
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const switchKind = (next: PostKind) => {
    setKind(next);
    setInlineError(null);
  };

  const handleFileSelected = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setInlineError(null);
    try {
      const { blob_path } = await uploader.upload(file);
      setBlobPath(blob_path);
      setPreviewUrl(URL.createObjectURL(file));
    } catch (e) {
      setBlobPath(null);
      setInlineError(e instanceof Error ? e.message : "Upload failed.");
    }
  };

  const removeImage = () => {
    setBlobPath(null);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    uploader.reset();
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setInlineError(null);

    let variables: CreatePostVariables;
    if (kind === "text") {
      variables = { kind: "text", body: body.trim() };
    } else if (kind === "text_image") {
      if (!blobPath) {
        setInlineError("Add an image first (or switch to a text post).");
        return;
      }
      variables = { kind: "text_image", body: body.trim(), media_blob_path: blobPath };
    } else if (kind === "twitch_clip") {
      variables = {
        kind: "twitch_clip",
        twitch_url: clipUrl.trim(),
        body: body.trim() || null,
      };
    } else {
      variables = {
        kind: "youtube_short",
        youtube_url: clipUrl.trim(),
        body: body.trim() || null,
      };
    }

    createPost.mutate(variables, {
      onSuccess: () => {
        toast.success("Posted.");
        resetForm();
      },
      onError: (error) => setInlineError(error.message),
    });
  };

  return (
    <section
      className="mb-8 rounded-lg border border-brand-gold-500/40 bg-brand-forest-800 p-6"
      data-testid="new-post-card"
    >
      <h2 className="m-0 font-heading text-xl text-brand-paper-100">
        Share something with your audience
      </h2>
      <p className="m-0 mt-1 font-body text-sm text-brand-paper-100/70">
        Your holders will see this immediately.
      </p>

      {/* Segmented content-type picker */}
      <div
        className="mt-4 inline-flex flex-wrap gap-1 rounded-md border border-brand-gold-500/20 p-1"
        role="tablist"
        aria-label="Post type"
      >
        {KIND_TABS.map((tab) => (
          <button
            key={tab.kind}
            type="button"
            role="tab"
            aria-selected={kind === tab.kind}
            onClick={() => switchKind(tab.kind)}
            className={[
              "rounded px-3 py-1.5 font-body text-sm transition-colors",
              kind === tab.kind
                ? "bg-brand-forest-700 text-brand-gold-300"
                : "text-brand-paper-100/70 hover:text-brand-paper-100",
            ].join(" ")}
            data-testid={`post-kind-${tab.kind}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3">
        {isClipKind ? (
          <>
            <input
              type="url"
              value={clipUrl}
              onChange={(e) => setClipUrl(e.target.value)}
              placeholder={
                kind === "twitch_clip"
                  ? "Paste Twitch clip URL (twitch.tv/... or clips.twitch.tv/...)"
                  : "Paste YouTube Short URL (youtube.com/shorts/... or youtu.be/...)"
              }
              className="w-full rounded border border-brand-gold-500/20 bg-brand-forest-900 px-3 py-2 font-mono text-sm text-brand-paper-100"
              data-testid="clip-url-input"
            />
            {showClipUrlError ? (
              <p
                className="m-0 font-body text-sm text-red-400"
                data-testid="clip-url-error"
                role="alert"
              >
                {kind === "twitch_clip"
                  ? "Not a recognized Twitch clip URL."
                  : "Not a recognized YouTube Short URL."}
              </p>
            ) : null}
            <p className="m-0 font-body text-sm text-brand-paper-100/60">
              Optionally add a short comment about the clip.
            </p>
          </>
        ) : null}

        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="What's on your mind?"
          rows={isClipKind ? 2 : 3}
          className="min-h-16 max-h-64 w-full resize-y rounded border border-brand-gold-500/20 bg-brand-forest-900 px-3 py-2 font-body text-sm text-brand-paper-100"
          data-testid="post-body-input"
        />
        <p
          className={[
            "m-0 text-right font-body text-sm",
            overLimit ? "text-red-400" : "text-brand-paper-100/60",
          ].join(" ")}
          data-testid="char-counter"
        >
          {body.length} / {BODY_MAX}
        </p>

        {kind === "text_image" ? (
          <div data-testid="image-drop-zone">
            {previewUrl ? (
              <div className="flex items-center gap-4">
                <img
                  src={previewUrl}
                  alt="Upload preview"
                  className="h-[200px] w-[200px] rounded object-cover"
                  data-testid="image-preview"
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={removeImage}
                  data-testid="remove-image-button"
                >
                  Remove image
                </Button>
              </div>
            ) : (
              <label className="block cursor-pointer rounded border border-dashed border-brand-gold-500/30 px-4 py-6 text-center font-body text-sm text-brand-paper-100/70 hover:border-brand-gold-500/60">
                {uploader.isUploading
                  ? `Uploading… ${uploader.progress}%`
                  : "Click to choose an image (JPEG, PNG, WebP — max 5 MB)"}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleFileSelected}
                  disabled={uploader.isUploading}
                  data-testid="image-file-input"
                />
              </label>
            )}
            {uploader.isUploading ? (
              <div
                className="mt-2 h-1 overflow-hidden rounded bg-brand-forest-900"
                data-testid="upload-progress"
              >
                <div
                  className="h-full bg-brand-gold-300 transition-all"
                  style={{ width: `${uploader.progress}%` }}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {inlineError ? (
          <p
            className="m-0 font-body text-sm text-red-400"
            data-testid="post-inline-error"
            role="alert"
          >
            {inlineError}
          </p>
        ) : null}

        <div>
          <Button
            type="submit"
            disabled={submitDisabled}
            data-testid="post-submit-button"
          >
            {createPost.isPending ? "Posting…" : "Post to feed"}
          </Button>
        </div>
      </form>
    </section>
  );
}
